import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { get } from "@/utils/service";
import { money } from "@/utils/api";
import RecordSelect from "./RecordSelect";
import AppSelect from "@/components/ui/app-select";

export interface LineDraft {
  key: string;
  product_id: string | null;
  section?: string;
  name: string;
  description: string;
  quantity: string;
  unit: string;
  unit_price: string;
  discount_percent: string;
  tax_rate_id: string;
}

interface TaxRate { id: string; name: string; rate: string }

export const newLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2), product_id: null, name: "", description: "", quantity: "1", unit: "", unit_price: "", discount_percent: "0", tax_rate_id: "",
});

export const linesFromApi = (items: Array<Record<string, any>> = []): LineDraft[] =>
  items.map((i) => ({
    key: i.id ?? Math.random().toString(36).slice(2), ...("section" in i ? { section: i.section ?? "" } : {}), product_id: i.product_id, name: i.name ?? "", description: i.description ?? "", quantity: String(Number(i.quantity)), unit: i.unit ?? "",
    unit_price: i.unit_price == null ? "" : String(i.unit_price), discount_percent: String(Number(i.discount_percent ?? 0)), tax_rate_id: i.tax_rate_id ?? "",
  }));

export const linesToApi = (lines: LineDraft[]) =>
  lines.filter((l) => l.name.trim() || l.product_id).map((l) => ({
    ...(l.section !== undefined ? { section: l.section || null } : {}), product_id: l.product_id, name: l.name || null, description: l.description || null, quantity: l.quantity || "1", unit: l.unit || null,
    unit_price: l.unit_price === "" ? null : l.unit_price, discount_percent: l.discount_percent || "0", tax_rate_id: l.tax_rate_id || null,
  }));

/** Estimate shown while editing; the server recalculates exactly (decimal arithmetic) on save. */
export function previewTotals(lines: LineDraft[], rates: TaxRate[]) {
  let subtotal = 0, discount = 0, tax = 0;
  for (const l of lines) {
    const s = Math.round(Number(l.quantity || 0) * Number(l.unit_price || 0) * 100) / 100;
    const d = Math.round(s * Number(l.discount_percent || 0)) / 100;
    const rate = Number(rates.find((r) => r.id === l.tax_rate_id)?.rate ?? 0);
    subtotal += s; discount += d; tax += Math.round((s - d) * rate) / 100;
  }
  return { subtotal, discount, tax, total: subtotal - discount + tax };
}

export function useTaxRates() {
  const [rates, setRates] = useState<TaxRate[]>([]);
  useEffect(() => { get<{ data: { tax_rates: TaxRate[] } }>("/tax-rates").then((r) => setRates(r.data.tax_rates)).catch(() => undefined); }, []);
  return rates;
}

const cell = "w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary";

export default function LineItemsEditor({ lines, onChange, currency, readOnly, sections = false, ratesOnly = false }: { lines: LineDraft[]; onChange: (l: LineDraft[]) => void; currency: string; readOnly?: boolean; sections?: boolean; ratesOnly?: boolean }) {
  const rates = useTaxRates();
  const update = (key: string, patch: Partial<LineDraft>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const totals = previewTotals(lines, rates);

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-2 font-medium">Item</th>
              {!ratesOnly && <th className="py-2 px-2 font-medium w-20">Qty</th>}
              <th className="py-2 px-2 font-medium w-32">{ratesOnly ? "Price per item" : "Price"}</th>
              <th className="py-2 px-2 font-medium w-20">Disc. %</th>
              <th className="py-2 px-2 font-medium w-36">Tax</th>
              {!ratesOnly && <th className="py-2 pl-2 font-medium w-28 text-right">Amount</th>}
              {!readOnly && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const sub = Number(l.quantity || 0) * Number(l.unit_price || 0);
              const amount = sub - (sub * Number(l.discount_percent || 0)) / 100;
              return (
                <tr key={l.key} className="border-b border-gray-100 align-top">
                  <td className="py-2 pr-2 min-w-[220px]">
                    {readOnly ? (
                      <>{sections && l.section && <p className="mb-1 text-[11px] text-primary">{l.section}</p>}<p className="font-medium text-gray-900">{l.name}</p>{l.description && <p className="text-gray-500">{l.description}</p>}</>
                    ) : (
                      <div className="space-y-1.5">
                        {sections && <input className={cell} placeholder="Section (e.g. Photography)" value={l.section ?? ""} onChange={(e) => update(l.key, { section: e.target.value })} />}
                        <RecordSelect light kind="products" params={{ currency }} value={null} placeholder={l.name ? "Replace with a catalogue item…" : "Pick from catalogue (optional)…"}
                          onChange={(p) => {
                            const raw = (p?.raw ?? {}) as Record<string, any>;
                            if (p) update(l.key, { product_id: p.id, name: raw.name ?? "", description: raw.description ?? "", unit: raw.unit ?? "", unit_price: String(raw.price ?? ""), tax_rate_id: raw.tax_rate_id ?? "" });
                          }} />
                        <input className={cell} placeholder="Name *" value={l.name} onChange={(e) => update(l.key, { name: e.target.value })} />
                        <input className={cell} placeholder="Description" value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} />
                      </div>
                    )}
                  </td>
                  {!ratesOnly && <td className="py-2 px-2">{readOnly ? Number(l.quantity) : <input type="number" min="0" step="any" className={cell} value={l.quantity} onChange={(e) => update(l.key, { quantity: e.target.value })} />}</td>}
                  <td className="py-2 px-2">{readOnly ? money(l.unit_price, currency) : <input type="number" min="0" step="0.01" className={cell} value={l.unit_price} onChange={(e) => update(l.key, { unit_price: e.target.value })} />}</td>
                  <td className="py-2 px-2">{readOnly ? `${Number(l.discount_percent)}%` : <input type="number" min="0" max="100" step="any" className={cell} value={l.discount_percent} onChange={(e) => update(l.key, { discount_percent: e.target.value })} />}</td>
                  <td className="py-2 px-2">
                    {readOnly ? (rates.find((r) => r.id === l.tax_rate_id)?.name ?? "—") : (
                      <AppSelect className={cell} value={l.tax_rate_id} onChange={(e) => update(l.key, { tax_rate_id: e.target.value })}>
                        <option value="">No tax</option>
                        {rates.map((r) => <option key={r.id} value={r.id}>{r.name} ({Number(r.rate)}%)</option>)}
                      </AppSelect>
                    )}
                  </td>
                  {!ratesOnly && <td className="py-2 pl-2 text-right font-medium text-gray-900">{money(amount, currency)}</td>}
                  {!readOnly && (
                    <td className="py-2 pl-1">
                      <button type="button" aria-label="Remove line" onClick={() => onChange(lines.filter((x) => x.key !== l.key))} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button type="button" onClick={() => onChange([...lines, newLine()])} className="mt-3 inline-flex items-center gap-1 rounded-lg border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10">
          <Plus className="w-3.5 h-3.5" /> Add line
        </button>
      )}
      {!ratesOnly && <div className="mt-4 ml-auto w-64 space-y-1 text-xs">
        <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{money(totals.subtotal, currency)}</span></div>
        {totals.discount > 0 && <div className="flex justify-between text-gray-600"><span>Discount</span><span>-{money(totals.discount, currency)}</span></div>}
        <div className="flex justify-between text-gray-600"><span>Tax</span><span>{money(totals.tax, currency)}</span></div>
        <div className="flex justify-between border-t border-gray-200 pt-1 text-sm font-semibold text-gray-900"><span>Total</span><span>{money(totals.total, currency)}</span></div>
      </div>}
    </div>
  );
}
